/**
 * The shapes the public website widgets share. They mirror the responses
 * of apps/api (`/api/events/finder`, `/api/groups`, `/api/search`,
 * `/api/locations`); there is no build-time link, so keep them in sync by
 * hand when a route changes.
 */

// --- Events (/api/events/finder, /api/search) ---

export interface FinderDate {
  id: number;
  /** MP wall clock, no zone ("2026-09-20T08:00:00") */
  start: string;
  end: string | null;
  featured: boolean;
  /** Registration is on, inside its window, and there is somewhere to register */
  registrationOpen: boolean;
  registrationStart: string | null;
  registrationEnd: string | null;
  capacity: number | null;
  registered: number;
  full: boolean;
  /** Only when this date has its own poster */
  imageUrl: string | null;
}

/** One card: a series (or a lone event) with its dates nested. */
/**
 * One way into an event: a student paying $10, a leader free, a volunteer
 * taking shifts. Almost every event has none, and means one.
 */
export interface WayIn {
  label: string;
  price: number;
  /** Serving, not attending. */
  serving: boolean;
}

export interface FinderEvent {
  key: string;
  seriesId: number | null;
  /** The first upcoming date's Event_ID */
  id: number;
  title: string;
  shortDescription: string | null;
  /** Plain text or WYSIWYG HTML, capped at 6000 chars */
  description: string | null;
  imageUrl: string | null;
  congregationId: number;
  congregationName: string;
  /** The venue: the event's own Location, else the campus building */
  locationName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  /** The venue is not the campus building */
  offsite: boolean;
  eventTypeId: number | null;
  eventType: string | null;
  programId: number | null;
  programName: string | null;
  ministryId: number | null;
  ministryName: string | null;
  productId: number | null;
  basePrice: number | null;
  externalRegistrationUrl: string | null;
  /** This event's own page on the site, when it has one. Links go here. */
  pageUrl: string | null;
  /**
   * The ways in, in the order staff put them. Empty means the event has one
   * way in and `basePrice` is the whole story.
   */
  waysIn: WayIn[];
  forceLogin: boolean;
  opportunityCount: number;
  count: number;
  firstStart: string;
  lastStart: string;
  dates: FinderDate[];
}

// --- Groups (/api/groups, /api/search) ---

export interface FinderLeader {
  name: string;
  imageUrl: string | null;
}

export interface FinderGroup {
  id: number;
  title: string;
  description: string;
  full: boolean;
  paused: boolean;
  day: string;
  occurrence: string;
  time: string;
  city: string;
  campusId: number | null;
  campusName: string | null;
  lifeStageId: number | null;
  lifeStage: string | null;
  /** true = kids welcome, false = adults only, null = not specified */
  kidsWelcome: boolean | null;
  householdTypeId: number | null;
  householdType: string | null;
  genderId: number | null;
  gender: string | null;
  leaders: FinderLeader[];
  /** Part of the See Jesus campaign (MP "See Jesus Group" website tag) */
  seeJesus: boolean;
  /** Extra search terms from Group_Metadata (e.g. "Spanish", "Español") */
  keywords: string[];
}

// --- The signed-in visitor's own groups (/api/groups/me) ---

export type GroupRole = 'leader' | 'member';

export interface MyGroupMember {
  contactId: number;
  name: string;
  imageUrl: string | null;
  role: GroupRole;
}

export interface MyGroupAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

export interface MyGroup {
  id: number;
  name: string;
  role: GroupRole;
  address: MyGroupAddress | null;
  members: MyGroupMember[];
  /** Deep link into MP Group Connect for this group */
  connectUrl: string;
}

export type InquiryStatus = 'pending' | 'placed' | 'closed';

/**
 * One Group_Inquiries row for someone in the signed-in visitor's household
 * (/api/groups/me). `status` reads MP's Placed bit: null = still waiting,
 * true = placed in the group, false = reviewed and closed.
 */
export interface MyInquiry {
  id: number;
  groupId: number;
  groupName: string;
  /** The household contact the row belongs to (the submitter, for an `other`) */
  contactId: number;
  /** Whose inquiry it is, as written on the row */
  name: string;
  /** The row is for someone outside MP, submitted by this contact */
  isOther: boolean;
  /** Inquiry_Date */
  date: string;
  status: InquiryStatus;
}

// --- Serve opportunities (/api/serve, /api/serve/[id], /api/search) ---
// Mirrors apps/api/src/lib/serveCatalog.ts; keep in sync by hand.

export interface ServeContact {
  contactId: number;
  name: string;
  imageUrl: string | null;
}

/** Serve_Teams: what kind of help a role is. Sorted by `sort`. */
export interface ServeTeam {
  id: number;
  name: string;
  description: string | null;
  sort: number;
}

/**
 * A named list staff keep in MP (Checklists + Checklist_Items) and attach to an
 * opportunity or care offer: what is in the box, what to bring.
 */
export interface Checklist {
  id: number;
  /** What the page calls it; null shows "Checklist" */
  heading: string | null;
  description: string | null;
  items: { id: number; item: string; detail: string | null }[];
}

export interface ServeAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

export interface ServeAttribute {
  id: number;
  name: string;
  typeId: number;
}

export interface FinderOpportunity {
  id: number;
  title: string;
  /** /serve/{slug}; null for nearly every opportunity */
  slug?: string | null;
  /** The Collection this is one way of helping with */
  parentId?: number | null;
  /** Has children: an umbrella with its own page, never signed up for itself */
  isCollection?: boolean;
  /** #RRGGBB the page wears, and something legible on it */
  accentColor?: string | null;
  accentInk?: string | null;
  checklist?: Checklist | null;
  /** WYSIWYG HTML or plain text, as staff typed it */
  description: string;
  typeId: number;
  /** "In the Church", "In the Community", "Around the World" */
  type: string;
  programId: number;
  programName: string;
  ministryId: number;
  ministryName: string;
  congregationId: number;
  congregationName: string;
  /** The campus building */
  locationName: string | null;
  /** MP wall clock; null for an ongoing role */
  date: string | null;
  /** "09:30:00" or null */
  shiftStart: string | null;
  shiftEnd: string | null;
  durationHours: number | null;
  publishDate: string;
  /** The opportunity's own image, else its ministry's */
  imageUrl: string | null;
  externalRegistrationUrl: string | null;
  roleId: number;
  roleTitle: string;
  backgroundCheck: boolean;
  contact: ServeContact | null;
  minimumAge: number | null;
  requiredGenderId: number | null;
  requiredGender: string | null;
  minimumNeeded: number | null;
  maximumNeeded: number | null;
  /** Responses that were not "Not Placed" */
  responded: number;
  /** maximumNeeded minus responded, when a maximum is set */
  remaining: number | null;
  customFormId: number | null;
  addToEventId: number | null;
  addToEventTitle: string | null;
  /** The service this shift serves, MP wall clock (serve-teams-1.0.sql) */
  addToEventStart?: string | null;
  /** The team it belongs to; the sign-up sheet asks the day, then the team */
  serveTeam?: ServeTeam | null;
  /** Locked for anyone without a current CPP (serve-cpp-1.0.sql) */
  cppRequired?: boolean;
  /** Who hears when somebody without one would like to serve; else the contact person */
  cppContact?: { contactId: number; name: string } | null;
  /** Cancel or change online until then, MP wall clock (serve-changes-1.0.sql) */
  changesClose?: string | null;
  /** MP emails the respondent itself when true */
  hasResponseMessage: boolean;
  closeResponses: boolean;
  /** The opportunity's own address, else the campus's */
  address: ServeAddress | null;
  attributes: ServeAttribute[];
}

/** A household member's recent serve response (/api/serve/me). */
export type ServeResponseStatus = 'pending' | 'placed' | 'declined' | 'closed';

export interface MyServeResponse {
  id: number;
  opportunityId: number;
  contactId: number;
  name: string;
  /** MP wall clock */
  date: string;
  status: ServeResponseStatus;
}

/** A custom form on an opportunity (/api/serve/[id]/form), as apps/api/src/lib/mpForms.ts shapes it. */
export type ServeFormFieldKind =
  | 'text'
  | 'memo'
  | 'date'
  | 'radio'
  | 'select'
  | 'instructions'
  | 'checkbox'
  | 'phone'
  | 'file';

export interface ServeFormField {
  id: number;
  order: number;
  /** Plain text */
  label: string;
  /** The label with markup worth showing (bold, a link, a break); null when plain */
  labelHtml: string | null;
  kind: ServeFormFieldKind;
  required: boolean;
  options: string[];
  hidden: boolean;
  /** A checkbox inside an Instructions group */
  parentId: number | null;
  /** Shown only when field `dependsOn` has the answer `dependsOnValue` */
  dependsOn: number | null;
  dependsOnValue: string | null;
}

export interface ServeForm {
  id: number;
  title: string;
  instructions: string | null;
  completeMessage: string | null;
  fields: ServeFormField[];
  /** False when a field kind cannot be rendered on the website */
  supported: boolean;
}

// --- Campuses (/api/locations, the subset the widgets use) ---

export interface LocationCongregation {
  id: number;
  name: string;
  display_name: string;
  campus_slug: string | null;
  service_times: string;
}

export interface LocationGroup {
  id: number;
  primary_congregation_id: number | null;
  svg_url: string | null;
  city: string;
  congregations: LocationCongregation[];
}

export function getPrimary(group: LocationGroup): LocationCongregation {
  if (group.primary_congregation_id) {
    const found = group.congregations.find((c) => c.id === group.primary_congregation_id);
    if (found) return found;
  }
  /*
   * A LocationGroup is a campus and the congregations that meet at it, so it
   * has at least one by construction and this cannot be undefined.
   *
   * The honest signature is `LocationCongregation | undefined`, and saying so
   * makes five widgets' CampusDropdown grow a guard. That is a worthwhile
   * change and a separate one; this note is here so the assertion is read as
   * a deferral rather than as nobody having noticed.
   */
  return group.congregations[0] as LocationCongregation;
}

export function getSubCongregations(group: LocationGroup): LocationCongregation[] {
  const primaryId = group.primary_congregation_id ?? group.congregations[0]?.id;
  return group.congregations.filter((c) => c.id !== primaryId);
}

/** Congregation_ID -> the location's brand SVG, for CampusSvgProvider. */
export function campusSvgMap(locations: LocationGroup[]): Map<number, string> {
  const map = new Map<number, string>();
  for (const group of locations) {
    if (!group.svg_url) continue;
    for (const c of group.congregations) map.set(c.id, group.svg_url);
  }
  return map;
}
